<template>
  <div class="static-full-events-calendar">
    <div class="static-full-events-calendar-container" v-show="!!weeks.length">
      <div ref="daysSwiper"></div>
      <div>
        <div class="header">
          <span class="header-title">
            <i class="far fa-calendar c-brand-color mr-1"></i>
            {{ calendarTitle }}
          </span>
        </div>
        <swiper class="swiper" ref="calendarSwiper" :options="swiperOption">
          <swiper-slide
            class="swiper-slide"
            v-for="(week, weekIndex) in weeks"
            :key="week.weekId"
          >
            <div class="week" v-if="showWeek(weekIndex)">
              <div
                v-for="day in week.weekDays"
                :key="day.date"
                class="day"
                @click="!day.isDisabled && selectDate(day)"
                :class="{
                  'day--active': day.dayId === activeDate,
                  'day--disabled': day.isDisabled,
                  'day--enabled': !day.isDisabled,
                  '': day.dayId === activeDate,
                }"
              >
                <span class="day-week-day hide-on-mobile">
                  {{ day.weekDay }}
                </span>
                <span class="day-week-day show-only-mobile">
                  {{ day.weekDayShort }}
                </span>
                <span class="day-day">
                  {{ day.day }}
                </span>
              </div>
            </div>
          </swiper-slide>
          <div
            class="swiper-button-prev-custom calendar-navigation-btn prev"
            :class="{
              disabled: !swiperActiveIndex,
              enabled: swiperActiveIndex,
            }"
            slot="button-prev"
          >
            <i class="far fa-long-arrow-left"></i>
          </div>
          <div
            class="swiper-button-next-custom calendar-navigation-btn next"
            :class="{
              disabled: isLastWeek,
              enabled: !isLastWeek,
            }"
            slot="button-next"
          >
            <i class="far fa-long-arrow-right"></i>
          </div>
        </swiper>
      </div>
      <div class="events-list-events" v-if="selectedDayEvents">
        <router-link
          class="undecorated-router-link"
          v-for="calendarEvent in selectedDayEvents"
          :key="calendarEvent.id"
          :to="{
            name: 'studio-event-details',
            params: { studio_url: studioURL, event_id: calendarEvent.id },
          }"
          :target="eventClickTarget"
        >
          <CompactEvent :event="calendarEvent" />
        </router-link>
      </div>
    </div>
    <div class="no-events" v-show="!weeks.length">No events scheduled.</div>
  </div>
</template>

<script>
import _ from 'lodash';
import { Swiper, SwiperSlide, directive } from 'vue-awesome-swiper';
import CompactEvent from './CompactEvent';
import {
  generateAllCalendarEvents,
  generateCalendarWeeks,
  groupCalendarEventsByDay,
  groupCalendarEventsByWeek,
  isSameDay,
} from '../../../core/calendar';
import { getStudioURL } from '@/web/router/custom-domain-utils';

export default {
  name: 'StaticFullEventsCalendar',
  components: {
    Swiper,
    SwiperSlide,
    CompactEvent,
  },
  directives: {
    swiper: directive,
  },
  props: {
    calendarEvents: {
      type: Array,
      default() {
        return [];
      },
    },
    hasMoreEvents: {
      type: Boolean,
      default() {
        return false;
      },
    },
    openInNewWindow: {
      type: Boolean,
      default() {
        return false;
      },
    },
    template: {
      type: String,
      default() {
        return '';
      },
    },
  },
  data() {
    return {
      swiperActiveIndex: 0,
      activeDate: null,
      swiperOption: {
        slidesPerView: 1,
        centeredSlides: true,
        spaceBetween: 0,
        navigation: {
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        },
      },
    };
  },
  computed: {
    eventClickTarget() {
      return this.openInNewWindow ? '_blank' : '';
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    allCalendarEvents() {
      let events = [];
      events = generateAllCalendarEvents(this.calendarEvents);
      return events;
    },
    selectedDayEvents() {
      if (!this.activeDate) return null;

      return this.allCalendarEvents.filter(event => {
        return isSameDay(event.date_start, this.activeDate);
      });
    },
    weeks() {
      const calendarEventsByWeek = groupCalendarEventsByWeek(
        this.allCalendarEvents
      );
      return generateCalendarWeeks(calendarEventsByWeek);
    },
    swiper() {
      return this.$refs.calendarSwiper.swiperInstance;
    },
    calendarTitle() {
      return this.weeks.length
        ? this.weeks[this.swiperActiveIndex].month
        : null;
    },
    isLastWeek() {
      return (
        this.swiperActiveIndex === this.weeks.length - 1 && !this.hasMoreEvents
      );
    },
  },
  methods: {
    action(event) {
      window.alert('Go to Event: ' + event.event_name);
    },
    showWeek(weekIndex) {
      return [weekIndex - 1, weekIndex, weekIndex + 1].includes(
        this.swiperActiveIndex
      );
    },
    selectDate(day) {
      if (!day) return;
      this.activeDate = day.dayId;
    },
    findUpcomingDayWithEvents() {
      let upcomingDayWithEvents = null;
      let upcomingDayWithEventsWeekIndex = null;
      this.weeks.find((week, weekIndex) => {
        upcomingDayWithEvents = week.weekDays.find(
          day => day.isUpcoming && day.eventsInDay > 0
        );
        if (upcomingDayWithEvents) {
          upcomingDayWithEventsWeekIndex = weekIndex;
          return true;
        }
      });
      if (!upcomingDayWithEvents) {
        upcomingDayWithEvents = _.last(
          _.last(this.weeks).weekDays.filter(day => day.eventsInDay > 0)
        );
        upcomingDayWithEventsWeekIndex = this.weeks.length - 1;
      }
      return {
        day: upcomingDayWithEvents,
        eventWeekIndex: upcomingDayWithEventsWeekIndex,
      };
    },
  },
  mounted() {
    this.swiper.on('slideChange', () => {
      this.swiperActiveIndex = this.swiper.activeIndex;
      if (
        this.swiperActiveIndex === this.weeks.length - 1 &&
        this.hasMoreEvents
      ) {
        this.$emit('load-more');
      }
    });
    const { day, eventWeekIndex } = this.findUpcomingDayWithEvents();
    if (day) {
      this.activeDate = day.dayId;
      this.swiper.slideTo(eventWeekIndex);
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.static-full-events-calendar-container {
  position: relative;
}

.show-only-mobile {
  @include media-breakpoint-up(md) {
    display: none;
  }
}

.hide-on-mobile {
  @include media-breakpoint-down(sm) {
    display: none;
  }
}

.swiper {
  .swiper-button-next:after,
  .swiper-container-rtl .swiper-button-prev:after,
  .swiper-button-prev:after,
  .swiper-container-rtl .swiper-button-next:after {
    font-size: 22px;
    font-weight: bold;
    display: none;
    @include media-breakpoint-up(md) {
      display: flex;
    }
  }
}

.swiper-slide {
  padding-top: 42px;
}

.header {
  position: absolute;
  width: 100%;
}

.header-title {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 42px;
  border: 1px solid $medium-gray;
  border-radius: 6px;
  margin: 0 64px;
}

.week {
  display: flex;
  justify-content: space-around;
}

.day {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-radius: 5px;
  height: 80px;
  width: 80px;
  max-width: 80px;
  margin: 10px 5px;
  flex: 1;

  @include media-breakpoint-up(md) {
    margin: 10px 15px;
  }

  &--active {
    .day-day {
      color: white;
      background-color: var(--brand-color);
      border-radius: 50%;
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &--enabled {
    cursor: pointer;

    &:hover {
      .day-day {
        color: white;
        background-color: rgba(#000, 0.05);
        border-radius: 50%;
        width: 2rem;
        height: 2rem;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }

  &--disabled {
    opacity: 0.5;
  }
}

.day-week-day {
  font-size: 10px;
  margin-bottom: 5px;
  font-weight: 500;
}

.day-day {
  font-size: 18px;
  font-weight: 500;
}

.list-end-space {
  padding-top: calc(100vh - 440px);
}

.calendar-navigation-btn {
  border-radius: 6px;
  border: 1px solid $medium-gray;
  background-color: white;
  position: absolute;
  top: 0;
  height: 42px;
  width: 42px;
  margin-top: 0;
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &.prev {
    left: 1px;
    right: auto;
  }

  &.next {
    right: 1px;
    left: auto;
  }

  &.enabled:hover {
    color: var(--brand-color, $coral);
    border-color: var(--brand-color, $coral);
    background-color: $white;
  }

  &.disabled {
    opacity: 0.5;
    cursor: default;
  }
}
</style>
